import { useState, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../lib/api';
import { 
  Plus, 
  Truck,
  Trash,
  PencilSimple,
  Phone,
  EnvelopeSimple,
  Globe,
  MapPin,
  Siren,
  CaretDown,
  CaretRight,
  IdentificationCard,
  User,
  ToggleRight,
  ToggleLeft
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: "plumbing", name: "VVS/Rörmokare" },
  { id: "electrical", name: "Elektriker" },
  { id: "heating", name: "Värme" },
  { id: "ventilation", name: "Ventilation" },
  { id: "locksmith", name: "Låssmed" },
  { id: "elevator", name: "Hiss" },
  { id: "cleaning", name: "Städ" },
  { id: "garden", name: "Trädgård/Mark" },
  { id: "construction", name: "Bygg/Snickeri" },
  { id: "glass", name: "Glas/Fönster" },
  { id: "security", name: "Larm/Säkerhet" },
  { id: "waste", name: "Avfall/Sophantering" },
  { id: "pest", name: "Skadedjur" },
  { id: "other", name: "Övrigt" }
];

const formatPhoneNumber = (value: string | undefined): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 8) return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
};

// Supplier type definition
interface SupplierData {
  id?: string;
  name?: string;
  org_number?: string;
  category?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  emergency_contact?: string;
  emergency_phone?: string;
  notes?: string;
  is_active?: boolean;
  is_emergency?: boolean;
  is_emergency_contact?: boolean;
}

interface SupplierFormProps {
  data: SupplierData;
  onChange: (field: string, value: string | boolean) => void;
  onSubmit: () => void;
  submitLabel: string;
}

// Memoized form component to prevent re-renders
const SupplierForm = memo(function SupplierForm({ data, onChange, onSubmit, submitLabel }: SupplierFormProps) {
  const handleChange = useCallback((field: string, value: string | boolean) => {
    onChange(field, value);
  }, [onChange]);

  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Företagsnamn *</Label>
          <Input
            value={data.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="AB Rörmokaren"
            data-testid="supplier-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Org.nummer</Label>
          <Input
            value={data.org_number || ''}
            onChange={(e) => handleChange('org_number', e.target.value)}
            placeholder="556123-4567"
            className="font-mono"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Kategori *</Label>
        <Select
          value={data.category || 'other'}
          onValueChange={(value) => handleChange('category', value)}
        >
          <SelectTrigger data-testid="supplier-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Kontaktperson</Label>
        <Input
          value={data.contact_person || ''}
          onChange={(e) => handleChange('contact_person', e.target.value)}
          placeholder="Anna Andersson"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>E-post</Label>
          <Input
            type="email"
            value={data.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="info@foretagat.se"
          />
        </div>
        <div className="space-y-2">
          <Label>Telefon</Label>
          <Input
            value={data.phone || ''}
            onChange={(e) => handleChange('phone', formatPhoneNumber(e.target.value))}
            placeholder="08-123 45 67"
          />
        </div>
      </div>

      <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
        <div className="flex items-center gap-2 text-red-700">
          <Siren size={18} />
          <span className="font-medium">Jour-information</span>
        </div>
        <div className="space-y-2">
          <Label>Jour-telefon</Label>
          <Input
            value={data.emergency_phone || ''}
            onChange={(e) => handleChange('emergency_phone', formatPhoneNumber(e.target.value))}
            placeholder="08-123 45 67"
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>Visa som jour-kontakt</Label>
            <p className="text-xs text-slate-500">Visas i listan med akutnummer</p>
          </div>
          <Switch
            checked={data.is_emergency_contact || false}
            onCheckedChange={(checked) => handleChange('is_emergency_contact', checked)}
            data-testid="supplier-emergency-toggle"
          />
        </div>
      </div>

      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-green-800">Aktiv leverantör</Label>
            <p className="text-xs text-green-600">Endast aktiva leverantörer kan kontaktas via systemet</p>
          </div>
          <Switch
            checked={data.is_active || false}
            onCheckedChange={(checked) => handleChange('is_active', checked)}
            data-testid="supplier-active-toggle"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Adress</Label>
        <Input
          value={data.address || ''}
          onChange={(e) => handleChange('address', e.target.value)}
          placeholder="Storgatan 1, 123 45 Stockholm"
        />
      </div>

      <div className="space-y-2">
        <Label>Anteckningar</Label>
        <Textarea
          value={data.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Ytterligare information om leverantören..."
          rows={3}
        />
      </div>

      <Button onClick={onSubmit} className="w-full" data-testid="save-supplier-button">
        {submitLabel}
      </Button>
    </div>
  );
});

export default function SuppliersPage() {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<SupplierData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedSuppliers, setExpandedSuppliers] = useState<Record<string, boolean>>({});
  
  const emptyForm: SupplierData = {
    name: '',
    category: 'other',
    contact_person: '',
    email: '',
    phone: '',
    emergency_phone: '',
    address: '',
    notes: '',
    is_emergency: false,
    is_active: false
  };
  
  const [formData, setFormData] = useState<SupplierData>(emptyForm);
  const [editingSupplier, setEditingSupplier] = useState<SupplierData | null>(null);

  const fetchSuppliers = useCallback(async () => {
    try {
      const { data } = await getSuppliers();
      // Client-side filter since getSuppliers doesn't support params yet
      const filtered = selectedCategory !== 'all' 
        ? data.filter(s => s.category === selectedCategory)
        : data;
      setSuppliers(filtered);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Stable callback for form changes
  const handleFormChange = useCallback((field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditChange = useCallback((field: string, value: string | boolean | string[]) => {
    setEditingSupplier(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  const handleCreate = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Fyll i namn och kategori');
      return;
    }
    
    try {
      await createSupplier(formData);
      toast.success('Leverantör tillagd');
      setDialogOpen(false);
      setFormData(emptyForm);
      fetchSuppliers();
    } catch (error) {
      toast.error('Kunde inte lägga till leverantör');
    }
  };

  const handleUpdate = async () => {
    if (!editingSupplier || !editingSupplier.name || !editingSupplier.id) {
      toast.error('Fyll i namn');
      return;
    }

    try {
      await updateSupplier(editingSupplier.id, editingSupplier);
      toast.success('Leverantör uppdaterad');
      setEditDialogOpen(false);
      setEditingSupplier(null);
      fetchSuppliers();
    } catch (error) {
      toast.error('Kunde inte uppdatera leverantör');
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Är du säker på att du vill ta bort denna leverantör?')) return;
    
    try {
      await deleteSupplier(id);
      toast.success('Leverantör borttagen');
      fetchSuppliers();
    } catch (error) {
      toast.error('Kunde inte ta bort leverantör');
    }
  };

  const toggleActive = async (supplier: SupplierData, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!supplier.id) return;
    try {
      await updateSupplier(supplier.id, { is_active: !supplier.is_active });
      toast.success(supplier.is_active ? 'Leverantör inaktiverad' : 'Leverantör aktiverad');
      fetchSuppliers();
    } catch (error) {
      toast.error('Kunde inte ändra status');
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openEdit = (supplier: SupplierData, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSupplier({ ...supplier });
    setEditDialogOpen(true);
  };

  const getCategoryName = (categoryId: string) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Leverantörer
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Hantera företag för drift, underhåll och jour
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setFormData(emptyForm);
        }}>
          <DialogTrigger asChild>
            <Button className="btn-press" data-testid="add-supplier-button">
              <Plus size={18} className="mr-2" />
              Lägg till
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Lägg till leverantör</DialogTitle>
            </DialogHeader>
            <SupplierForm
              data={formData}
              onChange={handleFormChange}
              onSubmit={handleCreate}
              submitLabel="Lägg till"
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <div className="card-flat">
        <div className="flex items-center gap-4">
          <Label className="text-sm text-slate-500">Filtrera:</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[200px]" data-testid="category-filter">
              <SelectValue placeholder="Alla kategorier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla kategorier</SelectItem>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="card-flat animate-pulse">
              <div className="h-5 w-48 bg-slate-200 rounded mb-2" />
              <div className="h-4 w-32 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      ) : suppliers.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state">
            <Truck size={48} className="empty-state-icon" />
            <p className="text-slate-500">Inga leverantörer ännu</p>
            <p className="text-sm text-slate-400 mt-1">Lägg till leverantörer för drift och jour</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {suppliers.map((supplier) => {
            const supplierId = supplier.id || '';
            const isExpanded = expandedSuppliers[supplierId];
            
            return (
              <div
                key={supplierId}
                className="card-flat overflow-hidden"
                data-testid={`supplier-card-${supplierId}`}
              >
                {/* Header */}
                <div
                  className="flex items-center gap-4 cursor-pointer select-none"
                  onClick={() => toggleExpand(supplierId)}
                >
                  <div className="text-slate-400">
                    {isExpanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
                  </div>
                  
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Truck size={20} className="text-slate-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{supplier.name}</span>
                      {supplier.is_emergency_contact && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                          <Siren size={12} />
                          Jour
                        </span>
                      )}
                      {supplier.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                          Aktiv
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">
                          Inaktiv
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500">
                      {getCategoryName(supplier.category || '')}
                    </div>
                  </div>
                  
                  {supplier.phone && (
                    <a 
                      href={`tel:${supplier.phone}`}
                      className="hidden sm:flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone size={14} />
                      {supplier.phone}
                    </a>
                  )}
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => toggleActive(supplier, e)}
                      className={`p-2 rounded-lg transition-colors ${
                        supplier.is_active 
                          ? 'text-green-500 hover:text-green-700 hover:bg-green-50' 
                          : 'text-slate-300 hover:text-green-500 hover:bg-green-50'
                      }`}
                      title={supplier.is_active ? 'Inaktivera' : 'Aktivera'}
                      data-testid={`supplier-toggle-${supplier.id}`}
                    >
                      {supplier.is_active ? <ToggleRight size={20} weight="fill" /> : <ToggleLeft size={20} />}
                    </button>
                    <button
                      onClick={(e) => openEdit(supplier, e)}
                      className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Redigera"
                    >
                      <PencilSimple size={18} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(supplier.id || '', e)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Ta bort"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
                
                {/* Expanded */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {supplier.contact_person && (
                        <div className="flex items-center gap-2 text-sm">
                          <User size={16} className="text-slate-400" />
                          <span className="text-slate-500">Kontakt:</span>
                          <span className="text-slate-900">{supplier.contact_person}</span>
                        </div>
                      )}
                      
                      {supplier.email && (
                        <a 
                          href={`mailto:${supplier.email}`}
                          className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600"
                        >
                          <EnvelopeSimple size={16} className="text-slate-400" />
                          {supplier.email}
                        </a>
                      )}
                      
                      {supplier.phone && (
                        <a 
                          href={`tel:${supplier.phone}`}
                          className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600"
                        >
                          <Phone size={16} className="text-slate-400" />
                          {supplier.phone}
                        </a>
                      )}
                      
                      {supplier.emergency_phone && (
                        <a 
                          href={`tel:${supplier.emergency_phone}`}
                          className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
                        >
                          <Siren size={16} />
                          <span className="font-medium">Jour: {supplier.emergency_phone}</span>
                        </a>
                      )}
                      
                      {supplier.address && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin size={16} className="text-slate-400" />
                          <span className="text-slate-600">{supplier.address}</span>
                        </div>
                      )}
                    </div>
                    
                    {supplier.notes && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">{supplier.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Redigera leverantör</DialogTitle>
          </DialogHeader>
          {editingSupplier && (
            <SupplierForm
              data={editingSupplier}
              onChange={handleEditChange}
              onSubmit={handleUpdate}
              submitLabel="Spara"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
